import React from 'react';
import { Zap, Calendar, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import ProgressBar from '@/components/ui/ProgressBar';
import { useAIUsageData } from '@/hooks/subscription/useAIUsageData';

const MiniAIUsageCard: React.FC = () => {
  const usageData = useAIUsageData();
  const navigate = useNavigate();

  // Format the billing cycle end date
  const formatCycleEndDate = () => {
    if (!usageData.currentCycleEnd) return 'N/A';
    try {
      const endDate = new Date(usageData.currentCycleEnd);
      return endDate.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric' 
      });
    } catch (err) {
      return 'N/A';
    }
  };

  if (usageData.isLoading) {
    return (
      <div className="px-3 py-2 space-y-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Loader2 className="w-3 h-3 animate-spin text-amber-500" />
            <span className="text-sm font-semibold text-gray-900 dark:text-white">AI Usage</span>
          </div>
          <span className="text-xs text-gray-500">Loading...</span>
        </div>
        <div className="h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full">
          <div className="h-1.5 bg-gray-300 dark:bg-gray-600 rounded-full w-1/3 animate-pulse"></div>
        </div>
        <div className="flex justify-between text-xs text-gray-500">
          <span>Loading...</span>
          <span>Loading...</span>
        </div>
      </div>
    );
  }

  if (usageData.error) {
    return (
      <div className="px-3 py-2 space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-sm font-semibold text-gray-900 dark:text-white">AI Usage</span>
          <span className="text-xs text-red-500">Unavailable</span>
        </div>
        <div className="h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full">
          <div className="h-1.5 bg-red-400 rounded-full w-0"></div>
        </div>
        <div className="flex justify-between text-xs text-gray-500">
          <span>Error loading data</span>
          <span>--</span>
        </div>
      </div>
    );
  }

  return (
    <div 
      onClick={() => navigate('/billing')}
      className="px-3 py-2 space-y-2 hover:bg-gray-50 dark:hover:bg-gray-800/50 rounded-lg transition-colors cursor-pointer"
    >
      {/* Header Row: AI Usage - X Cred Remains */}
      <div className="flex items-center justify-between">
        <span className="text-sm font-semibold text-gray-900 dark:text-white">AI Usage</span>
        <span className="text-xs text-gray-600 dark:text-gray-300 font-medium">
          {usageData.remainingBalance.toLocaleString()} Credits left
        </span>
      </div>
      
      {/* Progress Bar */}
      <div className="w-full">
        <ProgressBar 
          progress={usageData.usagePercentage} 
          showLabels={false}
          className={usageData.usagePercentage > 80 
            ? "from-red-500 to-orange-500 dark:from-red-600 dark:to-orange-600 h-1.5" 
            : "from-amber-500 to-orange-500 dark:from-amber-600 dark:to-orange-600 h-1.5"
          } 
        />
      </div>
      
      {/* Footer Row: X Cred used - Y days left */}
      <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
        <div className="flex items-center gap-1">
          <Zap className="w-3 h-3 text-amber-500" />
          <span>{usageData.monthlyUsage.toLocaleString()} Credits used</span>
        </div>
        <div className="flex items-center gap-1">
          <Calendar className="w-3 h-3 text-amber-500" />
          <span>{usageData.daysRemainingInCycle} days left</span>
        </div>
      </div>
    </div>
  );
};

export default MiniAIUsageCard; 